import { UnavailableDataPlaceholderText } from "constants/text.ts";

export const parseCustomDateString = (dateString: string): Date => {
	const date = new Date(dateString);

	if (Number.isNaN(date.getTime())) {
		throw new TypeError("Invalid date format. Expected ISO 8601 format (e.g., 2026-01-06T20:59:43.534709Z)");
	}

	return date;
};

export const formatMinutesAgo = (diffMinutes: number): string | null => {
	if (diffMinutes < 1) return "a minute ago";
	if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
	return null;
};

export const formatHoursAgo = (diffHours: number): string | null => {
	if (diffHours === 1) return "an hour ago";
	if (diffHours < 24) return `${diffHours} hours ago`;
	return null;
};

export const formatDaysAgo = (diffDays: number, pastDate: Date): string | null => {
	if (diffDays === 1) return "Yesterday";
	if (diffDays < 7) {
		const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
		const dayName = dayNames[pastDate.getDay()];
		return dayName ?? "a few days ago";
	}
	return null;
};

export const formatWeeksAgo = (diffDays: number): string | null => {
	const exactWeeks = diffDays / 7;
	if (exactWeeks < 1) return null;
	if (exactWeeks >= 4) return null;

	const weeks = Math.floor(exactWeeks);
	if (weeks === 1) return "a week ago";
	return `${weeks} weeks ago`;
};

export const formatMonthsAgo = (diffDays: number): string | null => {
	const exactMonths = diffDays / 30;
	if (exactMonths < 1) return null;
	if (exactMonths >= 12) return null;

	const months = Math.floor(exactMonths);
	if (months === 1) return "a month ago";
	return `${months} months ago`;
};

export const formatYearsAgo = (diffDays: number): string => {
	const exactYears = diffDays / 365;
	const years = Math.floor(exactYears);

	if (years === 1) return "a year ago";
	return `${years} years ago`;
};

export const formatTimeDifference = (
	diffMinutes: number,
	diffHours: number,
	diffDays: number,
	pastDate: Date
): string => {
	const minutesResult = formatMinutesAgo(diffMinutes);
	if (minutesResult) return minutesResult;

	const hoursResult = formatHoursAgo(diffHours);
	if (hoursResult) return hoursResult;

	const daysResult = formatDaysAgo(diffDays, pastDate);
	if (daysResult) return daysResult;

	const weeksResult = formatWeeksAgo(diffDays);
	if (weeksResult) return weeksResult;

	const monthsResult = formatMonthsAgo(diffDays);
	if (monthsResult) return monthsResult;

	return formatYearsAgo(diffDays);
};

export const getRelativeTimeString = (dateString: string | undefined): string => {
	if (!dateString) return UnavailableDataPlaceholderText;
	const pastDate = parseCustomDateString(dateString);
	const now = new Date();

	const diffMs = now.getTime() - pastDate.getTime();
	const diffSeconds = Math.floor(diffMs / 1000);
	const diffMinutes = Math.floor(diffSeconds / 60);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);

	return formatTimeDifference(diffMinutes, diffHours, diffDays, pastDate);
};
